<template>
  <div class="ds-page" v-if="!$store.getters.isForbidden">
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Companies</div>
          <h2 class="page-title">{{ parentCompanyName }}</h2>
          <p class="page-subtitle">Manage company-client profiles, agreement, policy, and employee access.</p>
        </div>
        <div class="col-auto ms-auto">
          <div class="cc-page__header-actions">
            <button class="ds-btn ds-btn--secondary" @click="getListCompanyClient">
              <i class="el-icon-refresh"></i>
              Refresh
            </button>
            <button v-if="canCreateCompanyClient" class="ds-btn ds-btn--blue" @click="handlerBtnCreateCompanyClient">
              <font-awesome-icon icon="plus" />
              Create Client
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="cc-page__table-head">
        <div>
          <div class="cc-page__table-title">Filters</div>
          <div class="cc-page__table-subtitle">Search by client name and filter by PKS period.</div>
        </div>
      </div>

      <div class="cc-page__filter-grid">
        <div class="ds-field">
          <label class="ds-field__label">Search Name</label>
          <el-input
            v-model.trim="filters.name"
            size="small"
            placeholder="Input company client name"
            clearable
            @keyup.enter.native="applyFilters"
          />
        </div>
        <div class="ds-field">
          <label class="ds-field__label">PKS Start Date</label>
          <el-date-picker
            class="w-100"
            v-model="filters.pks_start_date"
            size="small"
            type="date"
            placeholder="Pick start date"
            format="dd MMMM yyyy"
            value-format="yyyy-MM-dd"
            clearable
          />
        </div>
        <div class="ds-field">
          <label class="ds-field__label">PKS End Date</label>
          <el-date-picker
            class="w-100"
            v-model="filters.pks_end_date"
            size="small"
            type="date"
            placeholder="Pick end date"
            format="dd MMMM yyyy"
            value-format="yyyy-MM-dd"
            clearable
          />
        </div>
        <div class="cc-page__filter-actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="resetFilters">Reset</button>
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.table" @click="applyFilters">Apply</button>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-table-wrap">
        <table class="ds-table" v-loading="loading.table">
          <thead>
            <tr>
              <th>Client</th>
              <th>PIC Email</th>
              <th>Industry</th>
              <th class="text-right">Limit</th>
              <th>PKS Period</th>
              <th>Status</th>
              <th class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in displayList" :key="item.id">
              <td>
                <div class="cc-page__identity">
                  <span class="cc-page__identity-name">{{ item.name || "—" }}</span>
                  <span class="cc-page__identity-sub">ID #{{ item.id || "-" }}</span>
                </div>
              </td>
              <td>
                <div class="ds-user-cell">
                  <div class="ds-avatar" :class="dsAvatarVariant(item?.pic_name)">
                    {{ dsGetInitials(item?.pic_name) }}
                  </div>
                  <div>
                    <div class="ds-user-cell__name">{{ item?.pic_name || "—" }}</div>
                    <div class="ds-user-cell__sub">{{ item?.pic_email || "" }}</div>
                  </div>
                </div>
              </td>
              <td>{{ item.industry || "—" }}</td>
              <td class="text-right">{{ formatCurrency(item.company_policy?.max_balance_disbursement) }}</td>
              <td>
                <div class="cc-page__pks-cell">
                  <span>{{ formatPksPeriod(item) }}</span>
                  <span class="ds-pill cc-page__pks-pill" :class="pksEndBadgeClass(item)">
                    {{ pksEndBadgeLabel(item) }}
                  </span>
                </div>
              </td>
              <td>
                <span class="ds-pill" :class="dsStatusClass(item.status)">
                  {{ dsStatusLabel(item.status || "—") }}
                </span>
              </td>
              <td class="text-center">
                <el-dropdown trigger="hover" @command="handleActionCommand($event, item)" popper-class="cc-page__action-popper">
                  <button class="ds-btn ds-btn--secondary ds-btn--sm cc-page__action-trigger">
                    Actions <i class="el-icon-arrow-down el-icon--right"></i>
                  </button>
                  <el-dropdown-menu slot="dropdown" class="cc-page__action-menu">
                    <el-dropdown-item v-if="canEditCompanyClient" command="edit" :disabled="isExpiredClient(item)">
                      <div class="cc-page__action-item">
                        <span class="cc-page__action-icon"><font-awesome-icon :icon="['fas', 'pen']" /></span>
                        <span>Edit Company Client</span>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="employees" :disabled="isExpiredClient(item)">
                      <div class="cc-page__action-item">
                        <span class="cc-page__action-icon"><font-awesome-icon icon="users" /></span>
                        <span>View Employees</span>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item v-if="canEditCompanyClient" command="status" :disabled="isExpiredClient(item)">
                      <div class="cc-page__action-item">
                        <span class="cc-page__action-icon"><font-awesome-icon icon="toggle-on" /></span>
                        <span>Update Status</span>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item v-if="canEditCompanyClient" command="parent" :disabled="isExpiredClient(item)">
                      <div class="cc-page__action-item">
                        <span class="cc-page__action-icon"><font-awesome-icon icon="building" /></span>
                        <span>Update Parent</span>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item v-if="canEditCompanyClient" command="pks">
                      <div class="cc-page__action-item">
                        <span class="cc-page__action-icon"><font-awesome-icon icon="calendar-alt" /></span>
                        <span>Update PKS</span>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item v-if="canEditCompanyClient" command="agreement" :disabled="isExpiredClient(item)">
                      <div class="cc-page__action-item">
                        <span class="cc-page__action-icon"><font-awesome-icon icon="file-signature" /></span>
                        <span>Update Agreement</span>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item v-if="canEditCompanyClient" command="policy" :disabled="isExpiredClient(item)">
                      <div class="cc-page__action-item">
                        <span class="cc-page__action-icon"><font-awesome-icon icon="shield-alt" /></span>
                        <span>Update Policy</span>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item v-if="canEditCompanyClient" command="delete" :disabled="isExpiredClient(item)">
                      <div class="cc-page__action-item cc-page__action-item--danger">
                        <span class="cc-page__action-icon"><font-awesome-icon icon="trash" /></span>
                        <span>Delete</span>
                      </div>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </td>
            </tr>
          </tbody>
        </table>

        <el-empty v-if="!totalRowsDisplay" description="No company client found" :image="emptyStateImage" class="ds-empty" />
      </div>

      <pagination
        :page="page"
        :per_page="per_page"
        :list="displayList"
        :total_rows="totalRowsDisplay"
        @pageChange="pageChangeHandler"
      />
    </div>

    <create-company-client
      :is-shown="showModalClient"
      :mode="formMode"
      :client-data="selectedClient"
      @onHide="
        showModalClient = false;
        getListCompanyClient();
      "
    />

    <b-modal v-model="showUpdateStatus" title="Update Status" hide-footer @hide="onHide">
      <div class="company-client-modal">
        <div class="company-client-modal__intro">
          Update status for <strong>{{ selected.name || "selected client" }}</strong
          >.
        </div>
        <div class="ds-field">
          <label class="ds-field__label">Client Status</label>
          <el-select v-model="selected.status" size="small" class="w-100" placeholder="Select status" filterable>
            <el-option v-for="item in company_category_options" :key="item.value" :label="item.text" :value="item.value" />
          </el-select>
        </div>
        <div class="company-client-modal__actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="onHide">Cancel</button>
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.submit" @click="updateStatus">Submit</button>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="showUpdateParent" title="Update Parent" hide-footer @hide="onHide">
      <div class="company-client-modal">
        <div class="company-client-modal__intro">
          Set parent company for <strong>{{ selected.name || "selected client" }}</strong
          >.
        </div>
        <div class="ds-field">
          <label class="ds-field__label">Parent Company</label>
          <el-select v-model="selected.vendor_company_id" placeholder="Select parent" class="w-100" filterable>
            <el-option
              v-for="item in companies.rows"
              :key="item.company_id"
              :label="item.company_name"
              :value="item.company_id"
            />
          </el-select>
        </div>
        <div class="company-client-modal__actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="onHide">Cancel</button>
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.submit" @click="updateParent">Submit</button>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="showUpdatePolicy" title="Update Policy" hide-footer @hide="onHide">
      <div class="company-client-modal">
        <div class="company-client-modal__intro">
          Update disbursement policy for <strong>{{ selected.name || "selected client" }}</strong
          >.
        </div>
        <div class="ds-field">
          <label class="ds-field__label">Max Balance Disbursement</label>
          <el-input
            v-if="list_setting.company_policy"
            v-model="list_setting.company_policy.max_balance_disbursement"
            size="small"
            placeholder="Input max balance disbursement"
          />
        </div>
        <div class="company-client-modal__actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="onHide">Cancel</button>
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.submit" @click="updatePolicy">Submit</button>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="showUpdateAgreement" title="Update Agreement" hide-footer @hide="onHide">
      <div class="company-client-modal">
        <div class="company-client-modal__grid">
          <div class="ds-field company-client-modal__field-full">
            <label class="ds-field__label">PKS Number</label>
            <el-input v-model="selected.pks_number" size="small" placeholder="Input PKS number" />
          </div>
          <div class="ds-field">
            <label class="ds-field__label">PKS Start Date</label>
            <el-date-picker
              :picker-options="datePickerStartOptions"
              class="w-100"
              v-model="selected.pks_start_date"
              type="date"
              placeholder="Pick start date"
              format="dd MMMM yyyy"
              value-format="yyyy-MM-dd"
              @input="updateEndDatePickerOptions"
            />
          </div>
          <div class="ds-field">
            <label class="ds-field__label">PKS End Date</label>
            <el-date-picker
              :picker-options="datePickerEndOptions"
              :key="endDateKey"
              class="w-100"
              v-model="selected.pks_end_date"
              type="date"
              placeholder="Pick end date"
              format="dd MMMM yyyy"
              value-format="yyyy-MM-dd"
            />
          </div>
        </div>
        <div class="company-client-modal__actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="onHide">Cancel</button>
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.submit" @click="updateAgreement">Submit</button>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="showUpdatePks" title="Update PKS" hide-footer @hide="onHide">
      <div class="company-client-modal">
        <div class="company-client-modal__grid">
          <div class="ds-field company-client-modal__field-full">
            <label class="ds-field__label">PKS Number</label>
            <el-input v-model="selected.pks_number" size="small" placeholder="Input PKS number" />
          </div>
          <div class="ds-field">
            <label class="ds-field__label">PKS Start Date</label>
            <el-date-picker
              :picker-options="datePickerStartOptions"
              class="w-100"
              v-model="selected.pks_start_date"
              type="date"
              placeholder="Pick start date"
              format="dd MMMM yyyy"
              value-format="yyyy-MM-dd"
              @input="updateEndDatePickerOptions"
            />
          </div>
          <div class="ds-field">
            <label class="ds-field__label">PKS End Date</label>
            <el-date-picker
              :picker-options="datePickerEndOptions"
              :key="endDateKey"
              class="w-100"
              v-model="selected.pks_end_date"
              type="date"
              placeholder="Pick end date"
              format="dd MMMM yyyy"
              value-format="yyyy-MM-dd"
            />
          </div>
        </div>
        <div class="company-client-modal__actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="onHide">Cancel</button>
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.submit" @click="updatePks">Submit</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import {
  GET_COMPANY_CLIENT_SETTINGS,
  GET_COMPANY_CLIENTS_BY_VENDOR,
  DELETE_COMPANY_CLIENT_RECORD,
  UPDATE_COMPANY_CLIENT_STATUS,
  UPDATE_COMPANY_CLIENT_PARENT,
  UPDATE_COMPANY_CLIENT_PKS,
  UPDATE_COMPANY_CLIENT_POLICY,
  UPDATE_COMPANY_CLIENT_AGREEMENT,
} from "@/store/modules/company_clients";
import { GET_COMPANIES, GET_DETAIL_COMPANY } from "@/store/modules/companies";
import emptyStateImage from "@/assets/images/empty-state.png";
import popupErrorMessages from "@/library/popup-error-messages";
import Pagination from "@/ui/components/Pagination.vue";
import CreateCompanyClient from "../wps/CreateCompanyClient.vue";

export default {
  name: "CompanyClient",
  components: {
    Pagination,
    CreateCompanyClient,
  },
  metaInfo: {
    title: "Company Client",
  },
  computed: {
    currentRole() {
      return this.$store.getters.current_role;
    },
    canCreateCompanyClient() {
      return Boolean(this.currentRole?.can_create || this.currentRole?.page_name === "*");
    },
    canEditCompanyClient() {
      return Boolean(this.currentRole?.can_edit || this.currentRole?.page_name === "*");
    },
    parentCompanyName() {
      return this.$route.query.company_name || this.parent_company?.company_name || this.parent_company?.name || "Company Client";
    },
    hasActiveFilters() {
      return Boolean(this.filters.name || this.filters.pks_start_date || this.filters.pks_end_date);
    },
    displayList() {
      if (!this.hasActiveFilters) {
        return this.list;
      }

      const searchName = String(this.filters.name || "")
        .toLowerCase()
        .trim();
      const filterStart = this.filters.pks_start_date ? moment(this.filters.pks_start_date, "YYYY-MM-DD") : null;
      const filterEnd = this.filters.pks_end_date ? moment(this.filters.pks_end_date, "YYYY-MM-DD") : null;

      return (this.list || []).filter((item) => {
        const itemName = String(item?.name || "").toLowerCase();
        const itemStartRaw = item?.company_agreement?.pks_start_date || item?.pks_start_date;
        const itemEndRaw = item?.company_agreement?.pks_end_date || item?.pks_end_date;
        const itemStart = itemStartRaw ? moment(itemStartRaw) : null;
        const itemEnd = itemEndRaw ? moment(itemEndRaw) : null;

        const isNameMatch = !searchName || itemName.includes(searchName);
        const isStartMatch = !filterStart || (itemStart && itemStart.isSameOrAfter(filterStart, "day"));
        const isEndMatch = !filterEnd || (itemEnd && itemEnd.isSameOrBefore(filterEnd, "day"));

        return isNameMatch && isStartMatch && isEndMatch;
      });
    },
    totalRowsDisplay() {
      if (this.hasActiveFilters) {
        return this.displayList.length;
      }
      return this.total_rows;
    },
  },
  data() {
    return {
      showUpdateParent: false,
      showUpdatePolicy: false,
      showUpdateAgreement: false,
      showUpdatePks: false,
      showUpdateStatus: false,
      showModalClient: false,
      formMode: "create",
      selectedClient: null,
      emptyStateImage,
      list: [],
      list_setting: {},
      parent_company: {},
      total_rows: 0,
      per_page: 10,
      page: 1,
      loading: {
        submit: false,
        table: false,
        company: false,
      },
      companies: {
        rows: [],
        count: 0,
      },
      selected: {
        vendor_company_id: 0,
        name: "",
        status: "",
        pks_number: "",
        pks_start_date: moment().format("YYYY-MM-DD"),
        pks_end_date: moment().add(7, "days").format("YYYY-MM-DD"),
      },
      datePickerStartOptions: {
        disabledDate: (date) => moment(date).isBefore(moment(), "days"),
      },
      datePickerEndOptions: {
        disabledDate: (date) => moment(date).isBefore(moment(), "days"),
      },
      endDateKey: 0,
      filters: {
        name: "",
        pks_start_date: "",
        pks_end_date: "",
      },
      company_category_options: [
        { value: "ACTIVE", text: "ACTIVE" },
        { value: "NOT_ACTIVE", text: "NOT ACTIVE" },
        { value: "WAITING_APPROVAL", text: "WAITING APPROVAL" },
        { value: "DELETED", text: "DELETED" },
      ],
    };
  },
  async mounted() {
    await this.loadParentCompany();
    await this.getListCompanyClient();
  },
  methods: {
    async loadParentCompany() {
      if (this.$route.query.company_name) {
        this.parent_company = {
          company_name: this.$route.query.company_name,
        };
        return;
      }

      try {
        await this.$store.dispatch(GET_DETAIL_COMPANY, {
          id: this.$route.params.id,
        });
        this.parent_company = this.$store.getters.detail_company || {};
      } catch (error) {
        this.parent_company = {};
      }
    },
    formatCurrency(value) {
      return `Rp ${Number(value || 0).toLocaleString("id-ID")}`;
    },
    formatPksPeriod(item) {
      const start = item?.company_agreement?.pks_start_date || item?.pks_start_date;
      const end = item?.company_agreement?.pks_end_date || item?.pks_end_date;

      if (!start && !end) {
        return "—";
      }

      return `${start ? moment(start).format("DD MMM YYYY") : "—"} - ${end ? moment(end).format("DD MMM YYYY") : "—"}`;
    },
    pksEndBadgeClass(item) {
      const end = item?.company_agreement?.pks_end_date || item?.pks_end_date;
      if (!end) return "ds-pill--gray";

      const diffDays = this.getPksRemainingDays(item);

      if (diffDays < 0) return "ds-pill--danger";
      if (diffDays <= 10) return "ds-pill--warning";
      return "ds-pill--success";
    },
    pksEndBadgeLabel(item) {
      const end = item?.company_agreement?.pks_end_date || item?.pks_end_date;
      if (!end) return "No PKS End Date";

      const diffDays = this.getPksRemainingDays(item);

      if (diffDays < 0) return `Expired ${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? "s" : ""} ago`;
      if (diffDays === 0) return "Expires today";
      if (diffDays <= 10) return `${diffDays} day${diffDays > 1 ? "s" : ""} left`;
      return `${diffDays} day${diffDays > 1 ? "s" : ""} left`;
    },
    getPksRemainingDays(item) {
      const end = item?.company_agreement?.pks_end_date || item?.pks_end_date;
      if (!end) return null;

      const endDate = moment(end).startOf("day");
      const today = moment().startOf("day");
      return endDate.diff(today, "days");
    },
    isExpiredClient(item) {
      const diffDays = this.getPksRemainingDays(item);
      return diffDays !== null && diffDays < 0;
    },
    handleActionCommand(command, item) {
      const actionMap = {
        edit: () => this.handlerBtnUpdateCompanyClient(item),
        employees: () => this.handlerBtnList(item),
        status: () => this.handlerBtnStatus(item),
        parent: () => this.handlerBtnParent(item),
        pks: () => this.handlerBtnPks(item),
        agreement: () => this.handlerBtnAgreement(item),
        policy: () => this.handlerBtnPolicy(item),
        delete: () => this.handlerBtnDelete(item),
      };

      const action = actionMap[command];
      if (action) {
        action();
      }
    },
    async applyFilters() {
      this.page = 1;
      await this.getListCompanyClient();
    },
    async resetFilters() {
      this.filters = {
        name: "",
        pks_start_date: "",
        pks_end_date: "",
      };
      this.page = 1;
      await this.getListCompanyClient();
    },
    statusPillClass(status) {
      const normalizedStatus = String(status || "").toUpperCase();

      if (normalizedStatus === "ACTIVE") return "ds-pill--success";
      if (normalizedStatus === "WAITING_APPROVAL") return "ds-pill--warning";
      if (normalizedStatus === "DELETED") return "ds-pill--danger";
      return "ds-pill--gray";
    },
    async getListSetting(id) {
      await this.$store.dispatch(GET_COMPANY_CLIENT_SETTINGS, { id });
      const response = this.$store.getters.company_client_settings || {};
      this.list_setting = response.data || response || {};
    },
    async loadCompanies() {
      this.loading.company = true;
      try {
        await this.$store.dispatch(GET_COMPANIES, {
          page: 1,
          per_page: 20,
        });
        this.companies = this.$store.getters.companies;
      } finally {
        this.loading.company = false;
      }
    },
    updateEndDatePickerOptions() {
      if (!this.selected.pks_start_date) {
        return;
      }

      const selectedStartDate = moment(this.selected.pks_start_date, "YYYY-MM-DD");
      this.datePickerEndOptions = {
        disabledDate: (date) => {
          const selectedDate = moment(date);
          return selectedDate.isBefore(moment(), "days") || selectedDate.isBefore(selectedStartDate, "day");
        },
      };
      this.endDateKey += 1;
    },
    onHide() {
      this.showUpdateStatus = false;
      this.showUpdateParent = false;
      this.showUpdatePks = false;
      this.showUpdateAgreement = false;
      this.showUpdatePolicy = false;
    },
    async getListCompanyClient() {
      this.loading.table = true;
      try {
        const params = {
          id: this.$route.params.id,
          page: this.page,
          size: this.per_page,
          per_page: this.per_page,
        };

        if (this.filters.name) {
          params.search_keyword = this.filters.name;
        }
        if (this.filters.pks_start_date) {
          params.pks_start_date = this.filters.pks_start_date;
        }
        if (this.filters.pks_end_date) {
          params.pks_end_date = this.filters.pks_end_date;
        }

        try {
          await this.$store.dispatch(GET_COMPANY_CLIENTS_BY_VENDOR, params);
        } catch (error) {
          const hasValidationError = error?.response?.data?.error?.code === 400;
          if (!hasValidationError) {
            throw error;
          }
          await this.$store.dispatch(GET_COMPANY_CLIENTS_BY_VENDOR, {
            id: this.$route.params.id,
            page: this.page,
            size: this.per_page,
            per_page: this.per_page,
          });
        }

        const response = this.$store.getters.company_clients_by_vendor || {};
        this.list = response.data || response.rows || [];
        this.total_rows = response.total || response.count || this.list.length;
      } catch (error) {
        popupErrorMessages(error?.response?.data);
      } finally {
        this.loading.table = false;
      }
    },
    async pageChangeHandler(page) {
      this.page = page;
      await this.getListCompanyClient();
    },
    async handlerBtnDelete(item) {
      this.$confirm("You will delete the Company Client. Continue?", "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            try {
              await this.$store.dispatch(DELETE_COMPANY_CLIENT_RECORD, { id: item.id });
              this.$message({
                title: "Success",
                type: "success",
                message: "Company Client deleted",
              });
              await this.getListCompanyClient();
            } catch (err) {
              popupErrorMessages(err.response?.data);
            }
            instance.confirmButtonLoading = false;
          }
          done();
        },
      }).catch(() => {});
    },
    handlerBtnList(item) {
      this.selected = item;
      this.$router.push({
        name: "Company Client Employee",
        params: { id: item.id },
      });
    },
    handlerBtnStatus(item) {
      this.showUpdateStatus = true;
      this.selected = { ...item };
    },
    async handlerBtnParent(item) {
      this.showUpdateParent = true;
      this.selected = { ...item };
      await this.loadCompanies();
    },
    handlerBtnPks(item) {
      this.showUpdatePks = true;
      this.selected = { ...item };
    },
    async handlerBtnAgreement(item) {
      this.showUpdateAgreement = true;
      this.selected = { ...item };
    },
    async handlerBtnPolicy(item) {
      await this.getListSetting(item.id);
      this.showUpdatePolicy = true;
      this.selected = { ...item };
    },
    handlerBtnCreateCompanyClient() {
      this.formMode = "create";
      this.selectedClient = null;
      this.showModalClient = true;
    },
    handlerBtnUpdateCompanyClient(item) {
      this.formMode = "edit";
      this.selectedClient = item;
      this.showModalClient = true;
    },
    async updateStatus() {
      this.loading.submit = true;
      try {
        await this.$store.dispatch(UPDATE_COMPANY_CLIENT_STATUS, {
          id: this.selected.id,
          status: this.selected.status,
        });
        this.$message({
          title: "Success",
          type: "success",
          message: "Status successfully updated",
        });
        this.showUpdateStatus = false;
        await this.getListCompanyClient();
      } catch (err) {
        popupErrorMessages(err.response?.data);
      } finally {
        this.loading.submit = false;
      }
    },
    async updateParent() {
      this.loading.submit = true;
      try {
        await this.$store.dispatch(UPDATE_COMPANY_CLIENT_PARENT, {
          id: this.selected.id,
          vendor_company_id: this.selected.vendor_company_id,
        });
        this.$message({
          title: "Success",
          type: "success",
          message: "Parent successfully updated",
        });
        this.showUpdateParent = false;
        await this.getListCompanyClient();
      } catch (err) {
        popupErrorMessages(err.response?.data);
      } finally {
        this.loading.submit = false;
      }
    },
    async updatePks() {
      this.loading.submit = true;
      try {
        await this.$store.dispatch(UPDATE_COMPANY_CLIENT_PKS, {
          id: this.selected.id,
          pks_number: this.selected.pks_number,
          pks_start_date: this.selected.pks_start_date,
          pks_end_date: this.selected.pks_end_date,
        });
        this.$message({
          title: "Success",
          type: "success",
          message: "PKS successfully updated",
        });
        this.showUpdatePks = false;
        await this.getListCompanyClient();
      } catch (err) {
        popupErrorMessages(err.response?.data);
      } finally {
        this.loading.submit = false;
      }
    },
    async updateAgreement() {
      this.loading.submit = true;
      try {
        await this.$store.dispatch(UPDATE_COMPANY_CLIENT_AGREEMENT, {
          id: this.selected.id,
          pks_number: this.selected.pks_number,
          pks_start_date: this.selected.pks_start_date,
          pks_end_date: this.selected.pks_end_date,
          updated_by: {},
        });
        this.$message({
          title: "Success",
          type: "success",
          message: "Agreement successfully updated",
        });
        this.showUpdateAgreement = false;
        await this.getListCompanyClient();
      } catch (err) {
        popupErrorMessages(err.response?.data);
      } finally {
        this.loading.submit = false;
      }
    },
    async updatePolicy() {
      this.loading.submit = true;
      try {
        await this.$store.dispatch(UPDATE_COMPANY_CLIENT_POLICY, {
          id: this.selected.id,
          max_balance_disbursement: parseInt(this.list_setting.company_policy?.max_balance_disbursement, 10),
          alert_method: "email",
          total_limit_percentage: 10,
        });
        this.$message({
          title: "Success",
          type: "success",
          message: "Policy successfully updated",
        });
        this.showUpdatePolicy = false;
        await this.getListCompanyClient();
      } catch (err) {
        popupErrorMessages(err.response?.data);
      } finally {
        this.loading.submit = false;
      }
    },
  },
};
</script>

<style scoped>
.cc-page__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cc-page__table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 6px;
}

.cc-page__filter-card {
  margin-bottom: 14px;
}

.cc-page__filter-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 12px;
  padding: 8px 20px 18px;
  align-items: end;
}

.cc-page__filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.cc-page__table-title {
  font-size: 15px;
  font-weight: 700;
  color: #182433;
}

.cc-page__table-subtitle {
  font-size: 12px;
  color: #656d77;
  margin-top: 4px;
}

.cc-page__identity {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cc-page__identity-name {
  font-weight: 700;
  color: #182433;
}

.cc-page__identity-sub {
  font-size: 12px;
  color: #656d77;
}

.cc-page__action-trigger {
  min-width: 108px;
  justify-content: center;
}

.cc-page__pks-cell {
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
}

.cc-page__pks-pill {
  width: fit-content;
}

.cc-page__action-menu {
  padding: 8px;
}

.cc-page__action-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 190px;
  color: #1f2937;
}

.cc-page__action-item--danger {
  color: #c0392b;
}

.cc-page__action-icon {
  width: 18px;
  text-align: center;
}

.company-client-modal {
  padding: 6px 2px 2px;
}

.company-client-modal__intro {
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(32, 107, 196, 0.08);
  color: #304152;
  font-size: 13px;
}

.company-client-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.company-client-modal__field-full {
  grid-column: 1 / -1;
}

.company-client-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

@media (max-width: 900px) {
  .cc-page__filter-grid {
    grid-template-columns: 1fr;
  }

  .cc-page__filter-actions {
    justify-content: flex-start;
  }

  .company-client-modal__grid {
    grid-template-columns: 1fr;
  }
}
</style>
